import { useState } from 'react'
import * as PropTypes from 'prop-types'
import { formatInTimeZone } from 'date-fns-tz'
import { useTranslation } from 'react-i18next'

import Icon from '../../../../../../../components/Icons'
import RadioField from '../../../../../../../components/FormLegacy/FormFieldsV2/RadioField'
import TextAreaField from '../../../../../../../components/FormLegacy/FormFieldsV2/TextAreaField'
import { getChecklistTypeOptions } from '../../../../helpers'

const RevisionSummaryWindow = ({
  handleCancel,
  data,
  timezone,
  updateChecklist,
  setRevisionMode,
}) => {
  const { t } = useTranslation(['checklists-dashboard', 'common'])
  const checklistTypeOptions = getChecklistTypeOptions(t)
  const [isSubmitting, setSubmitting] = useState(false)
  const [notificationType, setNotificationType] = useState(null)
  const [descriptionNote, setDescriptionNote] = useState('')
  const { dueDate, resident } = data

  const handleSubmit = async () => {
    setSubmitting(true)
    const dataToSend = {
      ...data,
      status: 'Revision Requested',
      revisionRequestNote: descriptionNote,
      notifications: notificationType,
    }
    try {
      await updateChecklist(dataToSend)
    } catch ({ message }) {
      throw new Error(message)
    } finally {
      setSubmitting(false)
      setRevisionMode(false)
      handleCancel()
    }
  }

  const isDisabled = !descriptionNote || !notificationType

  return (
    <div className="revision-summary-window">
      <div className="revision-summary-window__container">
        <div className="window-header">
          <div className="left-side">
            <div className="title">{t('checklists-dashboard:revisionWindow.title')}</div>
          </div>
          <div className="right-side">
            <div className="navi-button" onClick={() => handleCancel()}>
              <Icon icon="cross" />
            </div>
          </div>
        </div>
        <div className="main-view">
          <div className="row-text">
            <span className="bold-text">
              {t('checklists-dashboard:revisionWindow.inspectionTypeLabel', {
                type: checklistTypeOptions.find((opt) => opt.value === data.type).label,
              })}
            </span>
            {t('checklists-dashboard:revisionWindow.forApartmentPrefix')}
            <span className="bold-text">{` ${resident.apartment}`}</span>.
          </div>
          <div className="row-text">
            {t('checklists-dashboard:revisionWindow.addressPrefix')}
            {` ${resident.street}`}
          </div>
          <div className="row-text">
            {t('checklists-dashboard:revisionWindow.residentPrefix')}
            <span className="bold-text">{` ${resident.firstName} ${resident.lastName}`}</span>
          </div>
          <div className="row-text">
            {t('checklists-dashboard:revisionWindow.dueDatePrefix')}
            <span className="bold-text">{` ${formatInTimeZone(
              dueDate,
              timezone,
              'MM/dd/yyyy',
            )}`}</span>
          </div>
          <div className="radio-buttons-section">
            <RadioField
              name={'revisionNotification-push'}
              value={'push'}
              checked={notificationType === 'push'}
              label={t('checklists-dashboard:revisionWindow.sendPushNotification')}
              onChange={() => setNotificationType('push')}
              disabled={isSubmitting}
            />
            <RadioField
              name={'revisionNotification-emial'}
              value={'email'}
              checked={notificationType === 'email'}
              label={t('checklists-dashboard:revisionWindow.sendEmailNotification')}
              onChange={() => setNotificationType('email')}
              disabled={isSubmitting}
            />
            <RadioField
              name={'revisionNotification-both'}
              value={'both'}
              checked={notificationType === 'both'}
              label={t('checklists-dashboard:revisionWindow.sendBothNotifications')}
              onChange={() => setNotificationType('both')}
              disabled={isSubmitting}
            />
          </div>
          <div className="header-text">
            {t('checklists-dashboard:revisionWindow.noteToResident', {
              name: `${resident.firstName} ${resident.lastName}`,
            })}
          </div>
          <TextAreaField
            name="revisionNote"
            maxLength="500"
            placeholder={t('checklists-dashboard:revisionWindow.revisionNotePlaceholder')}
            value={descriptionNote}
            onChange={(e) => setDescriptionNote(e.target.value)}
            disabled={isSubmitting}
          />
        </div>
        <div className="window-footer">
          <div className="footer-buttons">
            <button
              type="submit"
              className={`button button-gray is-block ${isSubmitting ? 'is-loading' : ''}`}
              onClick={() => handleCancel()}
              disabled={isSubmitting}
            >
              {t('common:actions.cancel')}
            </button>
            <button
              type="submit"
              className={`button button-primary is-block ${isSubmitting ? 'is-loading' : ''}`}
              onClick={handleSubmit}
              disabled={isSubmitting || isDisabled}
            >
              {t('checklists-dashboard:revisionWindow.sendNotificationButton')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

RevisionSummaryWindow.propTypes = {
  handleCancel: PropTypes.func,
  updateChecklist: PropTypes.func,
  setRevisionMode: PropTypes.func,
  data: PropTypes.object,
  timezone: PropTypes.string,
}

export default RevisionSummaryWindow
